import type { BaseEntity } from "../../UniversalCRMTable/types";

export interface SelectAllPagesOptions {
  pageSize?: number;
  maxPages?: number;
  onProgress?: (currentPage: number, totalPages: number, currentCount: number) => void;
  onError?: (error: Error, page: number) => void;
}

export interface SelectAllPagesResult<T extends BaseEntity> {
  allItems: T[];
  totalCount: number;
  pagesFetched: number;
  errors: Array<{ page: number; error: Error }>;
}

/**
 * Service to fetch all data across all pages for bulk actions
 */
export class SelectAllPagesService {
  /**
   * Fetch all data across all pages
   */
  static async fetchAllPages<T extends BaseEntity>(
    fetchPage: (
      page: number,
      pageSize: number
    ) => Promise<{ data: T[]; totalCount: number; hasNextPage: boolean }>,
    options: SelectAllPagesOptions = {}
  ): Promise<SelectAllPagesResult<T>> {
    const {
      pageSize = 100,
      maxPages = 1000, // Safety limit to prevent infinite loops
      onProgress,
      onError,
    } = options;

    const allItems: T[] = [];
    const errors: Array<{ page: number; error: Error }> = [];
    const currentPage = 1;
    let totalCount = 0;
    let hasNextPage = true;

    try {
      // Fetch first page to get total count
      const firstPageResult = await fetchPage(1, pageSize);
      totalCount = firstPageResult.totalCount;
      allItems.push(...firstPageResult.data);
      hasNextPage = firstPageResult.hasNextPage;

      onProgress?.(1, Math.ceil(totalCount / pageSize), allItems.length);

      // Calculate total pages needed
      const totalPages = Math.ceil(totalCount / pageSize);
      const pagesToFetch = Math.min(totalPages, maxPages);

      // Fetch remaining pages
      for (let page = 2; page <= pagesToFetch && hasNextPage; page++) {
        try {
          const pageResult = await fetchPage(page, pageSize);
          allItems.push(...pageResult.data);
          hasNextPage = pageResult.hasNextPage;

          onProgress?.(page, totalPages, allItems.length);

          // Add a small delay to prevent overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error) {
          const errorObj = { page, error: error as Error };
          errors.push(errorObj);
          onError?.(error as Error, page);

          // Continue with next page even if one fails
          console.error(`Failed to fetch page ${page}:`, error);
        }
      }

      return {
        allItems,
        totalCount,
        pagesFetched: Math.min(currentPage, pagesToFetch),
        errors,
      };
    } catch (error) {
      throw new Error(`Failed to fetch all pages: ${error}`);
    }
  }

  /**
   * Fetch all data with GraphQL-style pagination (cursor-based)
   */
  static async fetchAllPagesGraphQL<T extends BaseEntity>(
    fetchPage: (
      cursor?: string,
      limit?: number
    ) => Promise<{ data: T[]; nextCursor?: string; hasNextPage: boolean }>,
    options: SelectAllPagesOptions = {}
  ): Promise<SelectAllPagesResult<T>> {
    const { pageSize = 100, maxPages = 1000, onProgress, onError } = options;

    const allItems: T[] = [];
    const errors: Array<{ page: number; error: Error }> = [];
    let currentPage = 1;
    let nextCursor: string | undefined;
    let hasNextPage = true;

    try {
      // Fetch pages until no more data
      while (hasNextPage && currentPage <= maxPages) {
        try {
          const pageResult = await fetchPage(nextCursor, pageSize);
          allItems.push(...pageResult.data);
          nextCursor = pageResult.nextCursor;
          hasNextPage = pageResult.hasNextPage;

          onProgress?.(currentPage, -1, allItems.length); // -1 for unknown total
          currentPage++;

          // Add a small delay to prevent overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error) {
          const errorObj = { page: currentPage, error: error as Error };
          errors.push(errorObj);
          onError?.(error as Error, currentPage);

          // Continue with next page even if one fails
          console.error(`Failed to fetch page ${currentPage}:`, error);
          currentPage++;
        }
      }

      return {
        allItems,
        totalCount: allItems.length,
        pagesFetched: currentPage - 1,
        errors,
      };
    } catch (error) {
      throw new Error(`Failed to fetch all pages: ${error}`);
    }
  }

  /**
   * Create a progress callback for UI updates
   */
  static createProgressCallback(
    onProgress: (progress: {
      currentPage: number;
      totalPages: number;
      currentCount: number;
      percentage: number;
    }) => void
  ) {
    return (currentPage: number, totalPages: number, currentCount: number) => {
      const percentage = totalPages > 0 ? Math.round((currentPage / totalPages) * 100) : 0;
      onProgress({
        currentPage,
        totalPages,
        currentCount,
        percentage,
      });
    };
  }
}
